import React, { useState, useEffect, useCallback, useRef } from "react";
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable';
import chroma from "chroma-js";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import { mstore } from "../../../store";
import { secureFetch } from "../../../lib/api";
import endpoint from "../../../config/endpoint.json";
import { error } from "../../../lib/userAlerts";
import PrintDiv from "../../../components/LocationAnalyticsComponent/printDiv";
import Filter from "../../../components/LocationAnalyticsComponent/filter";

import checkAccess from '../../../lib/checkAccess';
import { Loading, Button, Text, Divider, Row, Tooltip } from "@nextui-org/react";
import { Download, Filter2 } from "react-iconly";
import LocationReport from "./LocationReport";
import SubPageHeader from "../../../components/SubPageHeader";

const scale = chroma.scale(["red", "orange", "lightgreen"]).domain([0, 2.5, 5]);

const createColumnDefs = () => {
  return [
    {
      headerName: "Stop ID",
      field: "stopId",
      width: 80,
      resizable: true
    },
    {
      headerName: "Rating",
      field: "averageRating",
      filter: "agNumberColumnFilter",
      width: 80,
      resizable: true,
      cellStyle: params => {
        const color = scale(params.value).hex()
        return {color: '#222', backgroundColor: color, textAlign: 'center'};
      },
    },
    {
      headerName: "Reviews",
      field: "number_of_reviews",
      filter: "agNumberColumnFilter",
      width: 80,
      cellStyle: params => {
        return {textAlign: 'center'};
      },
      resizable: true
    },
    {
      headerName: "Stop Name",
      field: "stop_name",
      width: 120,
      resizable: true
    },
    {
      headerName: "Address",
      field: "location",
      width: 200,
      resizable: true
    },
    {
      headerName: "City",
      field: "stopCity",
      width: 100,
      resizable: true
    },
    {
      headerName: "State",
      field: "stopState",
      width: 80,
      resizable: true
    },
    {
      headerName: "Zip",
      field: "zip",
      width: 80,
      resizable: true
    },
  ];
};

const addFooters = doc => {

  const pageCount = doc.internal.getNumberOfPages();
  const dt = new Date().toLocaleTimeString();

  for (var i = 1; i <= pageCount; i++) {

    /**
     * 
     * Page border
     * 
     */

    doc.rect(5, 5, doc.internal.pageSize.width - 10, doc.internal.pageSize.height - 10, 'S');

    /**
     * 
     * Font style.
     * 
     */
    doc.setTextColor(169,169,169);
    doc.setFont('helvetica', 'italic');
    doc.setPage(i);

    /**
     * 
     * Report generated at..
     * 
     */
    
    doc.setFontSize(8);
    doc.text('Report generated at ' +  dt + '. Page ' + String(i) + ' of ' + String(pageCount), doc.internal.pageSize.width / 2, 287, {
      align: 'center'
    });

    /**
     * 
     * Powered by..
     * 
     */
    doc.setFontSize(16)
    doc.text('Powered by MTT.', 10, 287);
  }
}

const pdfColumnDefs = [{
                          title: "Date",
                          dataKey: "createdAt"
                        },
                        {
                          title: "Driver-1",
                          dataKey: "driver1_name"
                        },
                        {
                          title: "Driver-2",
                          dataKey: "driver2_name"
                        },
                        {
                          title: "Comments",
                          dataKey: "comment"
                        }
                      ];

const pdfTitle ='Location Analytics Report';
const pdfDetailTitle ='Driver comments';
const pdfSummaryTitle ='Rating summary';

const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '1rem'
  },
  header: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    padding: '1.5rem',
    marginBottom: '0.5rem'
  },
  gridContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 2px rgba(0,0,0,0.1)',
    padding: '1rem',
    height: 'calc(100vh - 200px)',
    display: 'flex',
    flexDirection: 'column'
  },
  gridWrapper: {
    flex: 1,
    width: '100%',
    height: '100%'
  },
  detailsContainer: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 2px rgba(0,0,0,0.1)',
    padding: '1.5rem',
    marginLeft: '0.5rem',
    height: 'calc(100vh - 200px)',
    overflowY: 'auto'
  },
  filterButton: {
    backgroundColor: '#007bff',
    color: 'white',
    '&:hover': {
      backgroundColor: '#0056b3'
    }
  },
  downloadButton: {
    backgroundColor: '#28a745',
    color: 'white',
    '&:hover': {
      backgroundColor: '#218838'
    }
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    padding: '1rem',
    textAlign: 'center',
    fontWeight: '600',
    borderBottom: '2px solid #dee2e6'
  },
  tableCell: {
    padding: '1rem',
    textAlign: 'center',
    borderBottom: '1px solid #dee2e6'
  }
};

const LocationAnalytics = () => {
  const clientId = useSelector(state => state.client?.id || mstore.getPath(["user", "company_id"]));
  const Access = useSelector(state => state.navigation.Access);
  
  const gridApi = useRef(null);
  const columnApi = useRef(null);

  const [state, setState] = useState({
    locationList: [],
    driverCommentList: [],
    columnDefs: createColumnDefs(),
    dateRange: [
      {
        lable: "(All Dates)",
        value: 0,
      },
      {
        lable: "Today",
        value: 1,
      },
      {
        lable: "This Week",
        value: 7,
      },
      {
        lable: "This Month",
        value: 30,
      },
      {
        lable: "This Year",
        value: 365,
      },
    ],
    topSelection: [
      {
        lable: "Top 10",
        value: 10,
      },
      {
        lable: "Top 25",
        value: 25,
      },
      {
        lable: "Top 50",
        value: 50,
      },
      {
        lable: "Top 100",
        value: 100,
      },
      {
        lable: "Top 500",
        value: 500,
      },
      {
        lable: "Top 1000",
        value: 1000,
      },
      {
        lable: "All Data",
        value: 1000000,
      },
    ],
    topCategory: [
      {
        lable: "Best",
        value: "DESC",
      },
      {
        lable: "Worst",
        value: "ASC",
      },
    ],
    ratingCategoryList: [],
    focusAreaId: 0,
    dateRangeId: 0,
    startDate: "",
    endDate: "",
    topSelectionId: 10,
    topCategoryId: "DESC",
    selectedItem: false,
    headerArrayElement: [],
    ratings: [],
    locationName: "",
    locationAddress: "",
    stopCity: "",
    stopState: "",
    stopCountry: "",
    stopZip: "",
    stopName: "",
    loading: false,
    disableDownload: true,
    rowSelection: 'single',
    loadingDetails: false,
    displayValues: {
      focusArea: "Overall rating",
      topSelection: "Top 10",
      topCategory: "Best",
      dateRange: ""
    }
  });

  const getRatingCategories = async () => {
    try {
      const [ok, status, json] = await secureFetch(endpoint.getRatingCategoryList, {});
        if (ok) {
        setState(prev => ({ ...prev, ratingCategoryList: json.ratingCategoryList || [] }));
        } else {
        error("Oops! error occurred during fetching rating category list!");
        setState(prev => ({ ...prev, ratingCategoryList: [] }));
      }
    } catch (error) {
      console.error('Error fetching rating categories:', error);
      setState(prev => ({ ...prev, ratingCategoryList: [] }));
    }
  };

  const getDriverComments = useCallback(async (locationAddress) => {
    const [ok, status, json] = await secureFetch(endpoint.getDriverCommentList, {
      startDate: state.startDate,
      endDate: state.endDate,
      location: locationAddress,
    });
      if (ok) {
      setState(prev => ({ ...prev, driverCommentList: json.driverCommentList }));
      } else {
      error("Oops! error occurred during fetching rating category list!");
    }
  }, [state.startDate, state.endDate]);

  const getPointsPerChoice = useCallback(async (locationAddress) => {
    const [ok, status, json] = await secureFetch(endpoint.getPointsPerChoice, {
      startDate: state.startDate,
      endDate: state.endDate,
      location: locationAddress,
    });
      if (ok) {
      setState(prev => ({
        ...prev,
          headerArrayElement: json.headerArrayElement,
          ratings: json.ratings,
          disableDownload: false,
      }));
      } else {
      error("Oops! error occurred during fetching rating category list!");
    }
  }, [state.startDate, state.endDate]);

  const getLocationList = async () => {
    if (!state.ratingCategoryList) {
      console.warn('Rating categories not loaded yet');
      return;
    }

    setState(prev => ({ ...prev, loading: true }));
    
    try {
      const [ok, status, json] = await secureFetch(endpoint.getLocationList, {
        startDate: state.startDate,
        endDate: state.endDate,
        focusArea: state.focusAreaId,
        orderSequence: state.topCategoryId,
        limit: state.topSelectionId,
        stopName: state.stopName,
      });
      
      if (ok) {
      setState(prev => ({
        ...prev,
          locationList: json.locationList,
          loading: false,
      }));
      } else {
      error("Oops! error occurred during fetching location list!");
      setState(prev => ({ ...prev, loading: false }));
    }
    } catch (error) {
      console.error('Error fetching location list:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    const initializeDisplayValues = async () => {
      await checkAccess(clientId);
      await getRatingCategories();
      
      // Set initial display values after rating categories are loaded
      setState(prev => {
        const focusArea = prev.ratingCategoryList?.find((item) => item.id == prev.focusAreaId);
        const topSelection = prev.topSelection.find((item) => item.value == prev.topSelectionId);
        const topCategory = prev.topCategory.find((item) => item.value == prev.topCategoryId);
        
        return {
          ...prev,
          displayValues: {
            focusArea: focusArea ? focusArea.cat_desc : "Overall rating",
            topSelection: topSelection ? topSelection.lable : "",
            topCategory: topCategory ? topCategory.lable : "",
            dateRange: [prev.endDate, prev.startDate].filter(Boolean).join(" - ")
          }
        };
      });
      
      getLocationList();
    };

    initializeDisplayValues();
  }, [clientId]);

  const onGridReady = useCallback((params) => {
    gridApi.current = params.api;
    columnApi.current = params.columnApi;
  }, []);

  const fetchLocationDetails = useCallback(async (locationData) => {
    try {
      setState(prev => ({
        ...prev,
        selectedItem: true,
        locationName: locationData.stop_name,
        locationAddress: locationData.location,
        stopCity: locationData.stopCity,
        stopState: locationData.stopState,
        stopCountry: locationData.stopCountry,
        stopZip: locationData.zip,
        disableDownload: true,
        loadingDetails: true
      }));

      await getDriverComments(locationData.location);
      await getPointsPerChoice(locationData.location);
      
      setState(prev => ({ ...prev, loadingDetails: false }));
    } catch (error) {
      console.error('Error fetching location details:', error);
      setState(prev => ({ ...prev, loadingDetails: false }));
    }
  }, [getDriverComments, getPointsPerChoice]);

  const onRowClicked = useCallback((params) => {
    if (params.data) {
      fetchLocationDetails(params.data);
    }
  }, [fetchLocationDetails]);

  const handleSubmit = () => {
    if (!state.ratingCategoryList) {
      error("Please wait for the data to load");
      return;
    }

    const focusArea = state.ratingCategoryList.find((item) => item.id == state.focusAreaId);
    const topSelection = state.topSelection.find((item) => item.value == state.topSelectionId);
    const topCategory = state.topCategory.find((item) => item.value == state.topCategoryId);
    
    setState(prev => ({
      ...prev,
      displayValues: {
        focusArea: focusArea ? focusArea.cat_desc : "Overall rating",
        topSelection: topSelection ? topSelection.lable : "",
        topCategory: topCategory ? topCategory.lable : "",
        dateRange: [state.endDate, state.startDate].filter(Boolean).join(" - ")
      }
    }));

    getLocationList();
  };

  const renderLocationList = () => {
    const { displayValues } = state;
    const filterHeader = `${displayValues.focusArea}, ${displayValues.topSelection} - ${displayValues.topCategory}`;

    return (
      <div style={styles.gridContainer}>
        <div className="mb-4">
          <Text h4 className="text-primary">
            {displayValues.dateRange ? `${filterHeader}, ${displayValues.dateRange}` : filterHeader}
          </Text>
        </div>
        <div style={styles.gridWrapper}>
          <AgGridReact
            columnDefs={state.columnDefs}
            rowData={state.locationList}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            rowSelection={state.rowSelection}
            enableColResize
            enableSorting
            enableFilter
            pagination={true}
            paginationAutoPageSize={true}
            className="ag-theme-quartz"
            suppressRowClickSelection={true}
            stopEditingWhenCellsLoseFocus={true}
          />
        </div>
      </div>
    );
  }

  const renderFilterSection = () => {
    const {
      ratingCategoryList,
      dateRange,
      topSelection,
      topCategory,
      focusAreaId,
      dateRangeId,
      topSelectionId,
      topCategoryId,
      startDate,
      endDate,
      stopName
    } = state;

    const updateFilterState = (field, value) => {
      setState(prev => ({ ...prev, [field]: value }));
    };

    return (
      <div className="flex items-center gap-2">
          <Tooltip
          css={{ br: 0 }}
            className="pointer"
          placement="leftStart"
          trigger="click"
            content={
              <Filter
              ratingCategoryList={ratingCategoryList || []}
              dateRange={dateRange || []}
              topSelection={topSelection || []}
              topCategory={topCategory || []}
              focusAreaId={focusAreaId}
              dateRangeId={dateRangeId}
              topSelectionId={topSelectionId}
              topCategoryId={topCategoryId}
              startDate={startDate}
              endDate={endDate}
              stopName={stopName}
              setValueToState={updateFilterState}
              handleSubmit={handleSubmit}
              />
            }
          >
            <Button
              auto
            css={styles.filterButton}
            icon={<Filter2 set="light" />}
            >
            <Text color="white" size="1rem">Filter Results</Text>
            </Button>
          </Tooltip>
      </div>
    );
  }

  const downloadPDF = () => {
    let locationText = [
      state.locationAddress,
      state.stopCity,
      state.stopState,
      state.stopCountry,
      state.stopZip,
    ]
      .filter(Boolean)
      .join(", ");

    let dateRange = [ 
      state.endDate, 
      state.startDate
    ]
    .filter(Boolean)
    .join(" - ");

    dateRange = dateRange ? 'Between (' + dateRange + ')' : '';
    var doc = new jsPDF();
    let generatedText = 'Report generated for ' + mstore.get("user", "companyName");

    /**
     * 
     * Generated text
     * 
     */

    doc.setFontSize(12);
    doc.setTextColor(169,169,169);
    doc.setFont("Helvetica","italic");
    doc.text(generatedText, 200, 10, 'right');

    doc.setFont("Helvetica", "bold");

    /**
     * 
     * PDF header.
     * 
     */

    doc.setTextColor(0,0,0);
    doc.setFontSize(16);
    doc.text(10, 20, pdfTitle + '  ' + dateRange);

     /**
     * 
     * Location address
     * 
     */

    doc.setFontSize(12);
    doc.setFont("Helvetica","bold");
    doc.text(10, 30, state.locationName);

    doc.setFontSize(9);
    doc.setFont("Helvetica","italic");
    doc.text(10, 35, locationText);

    doc.setFontSize(14);
    doc.setFont("Helvetica","bold");
    doc.text(10, 45, pdfSummaryTitle);

    /**
     * 
     * Location summary table.
     * 
     */
    var res = doc.autoTableHtmlToJson(document.getElementById("loc-analytics-summary"));
    doc.autoTable(res.columns, res.data, {
      startY: 50,
      theme: 'striped',
      headStyles: { fontSize: 10, halign: 'center' },
      bodyStyles: { fontSize: 11, fontStyle: 'italic', overflow: 'linebreak', halign: 'center' },    
      margin: {horizontal: 10},
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      didParseCell: function (data) {
        if(data.section === 'body'){
          data.cell.styles.fillColor = scale(data.cell.raw.textContent).hex();
        }       
      },
    });

    /**
     * 
     * Driver comments header.
     * 
     */

    let finalY = doc.lastAutoTable.finalY; // The y position on the page
    doc.setFontSize(14);
    doc.text(10, finalY + 10, pdfDetailTitle);
      
    doc.autoTable(pdfColumnDefs, state.driverCommentList, {
      startY: finalY + 15,
      theme: 'striped',
      showHead: 'everyPage',
      headStyles: { fontSize: 10, halign: 'center' },
      bodyStyles: { fontSize: 8, fontStyle: 'italic', overflow: 'linebreak', halign: 'center' },
      columnStyles:{
        "comment": {halign:'left'}
      },    
      margin: {horizontal: 10},
      pageBreak: 'auto',
      rowPageBreak: 'avoid',
      didDrawPage: function (data) {
        // Re-set top margin. The change will be reflected only after print the first page.
        data.settings.margin.top = 10; 
      }
    });
    
    addFooters(doc);
    doc.save('mtt_location_analtyics_report.pdf')
  };

  const renderDetailsSection = () => {
    if (state.selectedItem) {
      const addressText = [
        state.locationAddress,
        state.stopCity,
        state.stopState,
        state.stopCountry,
        state.stopZip,
      ].filter(Boolean).join(", ");

      return (
        <div style={styles.detailsContainer}>
          <div className="flex justify-between items-center mb-4">
            <div>
              <Text h3 className="text-primary mb-2">{state.locationName}</Text>
              <Text className="text-gray-600">{addressText}</Text>
            </div>
            <Button
              size="sm"
              auto
              css={styles.downloadButton}
              icon={<Download set="light" size={20} />}
              disabled={state.disableDownload}
              onClick={downloadPDF}
            >
              <Text color="white">Download PDF</Text>
            </Button>
          </div>

          {state.loadingDetails ? (
            <div className="flex justify-center items-center h-[calc(100%-100px)]">
              <Loading size="lg" color="primary">
                Loading location details
              </Loading>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table style={styles.table}>
            <thead>
              <tr>
                      {state.headerArrayElement.map((item, index) => (
                        <th key={index} style={styles.tableHeader}>
                      {item}
                    </th>
                      ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                      {state.ratings.map((item, index) => (
                    <td
                      key={index}
                      style={{
                            ...styles.tableCell,
                        backgroundColor: scale(item).hex(),
                      }}
                    >
                      {item}
                    </td>
                      ))}
              </tr>
            </tbody>
          </table>
              </div>

              <div className="mt-2">
                <LocationReport 
                  driverCommentList={state.driverCommentList} 
                  selectedItem={state.selectedItem}
                />
              </div>
            </>
          )}
        </div>
      );
  }

    return (
      <div style={styles.detailsContainer} className="flex items-center justify-center">
        <Text h4 className="text-gray-500">
          Please select a location from the list
        </Text>
            </div>
    );
  }

  return (
    <div style={styles.container}>
      {state.loading ? (
        <div className="flex justify-center items-center h-screen">
          <Loading size="lg" color="primary">
            Fetching location data
          </Loading>
          </div>
        ) : (
          <div>
          <div style={styles.header}>
            <Row justify="space-between" align="center">
              <SubPageHeader>Ratings & Location Analytics</SubPageHeader>
              {renderFilterSection()}
            </Row>
          </div>
          
            <div className="flex">
            <div className="w-1/2">{renderLocationList()}</div>
            <div className="w-1/2">{renderDetailsSection()}</div>
              </div>

            <div style={{ display: "none" }}>
              <PrintDiv
              headerArrayElement={state.headerArrayElement}
              ratings={state.ratings}
              driverCommentList={state.driverCommentList}
              locationName={state.locationName}
              locationAddress={state.locationAddress}
              stopCity={state.stopCity}
              stopState={state.stopState}
              stopCountry={state.stopCountry}
              stopZip={state.stopZip}
              />
            </div>
          </div>
        )}
      </div>
    );
}

export default LocationAnalytics;
